import {
  axiosInstance,
  ColDef,
  ColumnApi,
  ColumnState,
  ExportFormatEnum,
  ExportType,
  FILE_TYPE,
  GridApi,
  saveFile,
} from '@fides/fides-react-components';
import { AxiosResponse } from 'axios';
import { GetRowNodeIdFunc, IRowNode } from '@ag-grid-community/core';
import { formatDate } from 'core/helpers/formatters';
import { Log } from '../helpers/initApp';
import { endpoints, exportDateFormat } from '../consts';
import { ChangeDTOExport, ColumnDefExport, DataExport, FiltersParams } from '../interfaces';
import { CreditTransferOrder, ExportSettings } from '../models';
import { CreditTransferReportType } from '../enums';
import { JsonObject } from '../types';
import { DelimiterType } from '../enums/DelimiterType';
import { mapSortModel } from '../helpers';
import { mapFilterModel } from '../../components/grid/hooks/useDataSource';

type Params = Record<string, any>;

export const getFileBody = (
  dataExport: DataExport,
  exportSettings: ExportSettings,
  selectedExportFormatFromDialog: ExportType
): DataExport => {
  const exportData: DataExport = { ...dataExport };

  exportData.delimiter = exportSettings?.delimiter
    ? DelimiterType[exportSettings?.delimiter]
    : DelimiterType.SETTINGS_EXPORTDELIMITER_OPTION_COMMA;

  const selectedExportFormatFromSettings: ExportType =
    exportSettings?.exportFormat === ExportFormatEnum.XLSX ? ExportType.XLSX : ExportType.CSV;
  exportData.exportType = selectedExportFormatFromDialog || selectedExportFormatFromSettings;

  return exportData;
};

export const downloadFile = async (
  endpoint: string,
  fileName: string,
  fileBody: DataExport,
  sortModel: string[],
  filter?: FiltersParams
): Promise<void> => {
  const queryParams: string = [
    `csvbody=${encodeURIComponent(JSON.stringify(fileBody))}`,
    filter && `filter=${encodeURIComponent(JSON.stringify(filter))}`,
    sortModel.length > 0 && `sort=${sortModel[0]}`,
  ]
    .filter(Boolean)
    .join('&');

  const url = `${endpoints.EFTApiUrl}/${endpoint}/${endpoints.export}?${queryParams}`;

  try {
    const response: AxiosResponse = await axiosInstance.get(url, {
      responseType: 'arraybuffer',
    });
    return saveFile(
      response.data,
      `${fileName}${formatDate(new Date(), exportDateFormat)}`,
      FILE_TYPE[fileBody.exportType]
    );
  } catch (error) {
    Log.error('Grid Service -> downloadFile', error);
    throw new Error((error as Error).message);
  }
};

export const executeExport = (
  selectedNodes: IRowNode[],
  isAllSelected: boolean,
  selectedExportFormatFromDialog: ExportType,
  columnApi: ColumnApi,
  gridApi: GridApi,
  exportSettings: ExportSettings,
  endpoint: string,
  fileName: string,
  columns?: ColumnDefExport[],
  extraFilterParams?: object,
  getRowIdPath?: GetRowNodeIdFunc
): void => {
  const columnsToExport = (columns || columnApi.getDisplayedCenterColumns())
    .map((column) => column.getColDef())
    .filter((colDef: ColDef) => !!colDef.headerName)
    .map((colDef: ColDef) => ({
      headerName: colDef.headerName,
      colIdExport: (colDef as any).colIdExport || colDef.colId,
    }));

  const sortedColumn: ColumnState[] = columnApi.getColumnState().filter((col: ColumnState) => col.sort !== null);
  const sortModel: string[] = mapSortModel(columnApi.getDisplayedCenterColumns(), sortedColumn);
  const dataExport: DataExport = {
    headers: columnsToExport.map((col: ColumnDefExport) => col.headerName),
    fields: columnsToExport.map((col: ColumnDefExport) => col.colIdExport),
    ...(!isAllSelected && {
      ids: selectedNodes?.map((row: IRowNode) => (getRowIdPath ? getRowIdPath(row) : row.data.uniqueId)),
    }),
  };
  const fileBody: DataExport = getFileBody(dataExport, exportSettings, selectedExportFormatFromDialog);
  const filterModel: { [p: string]: any } = gridApi.getFilterModel();
  const filterParams: FiltersParams = isAllSelected
    ? { ...mapFilterModel(filterModel, gridApi.getColumnDefs()), ...extraFilterParams }
    : null;

  downloadFile(endpoint, fileName, fileBody, sortModel, filterParams);
};

export const downloadClientSideData = async (
  endpoint: string,
  fileName: string,
  fileBody: DataExport,
  rows: ChangeDTOExport[]
): Promise<void> => {
  const url = `${endpoints.EFTApiUrl}/${endpoint}/${endpoints.exportClientSide}`;
  const params: Params = {
    csvbody: fileBody,
  };

  try {
    const response: AxiosResponse = await axiosInstance.post(url, rows, {
      params,
      responseType: 'arraybuffer',
    });
    return saveFile(
      response.data,
      `${fileName}${formatDate(new Date(), exportDateFormat)}`,
      FILE_TYPE[fileBody.exportType]
    );
  } catch (error) {
    Log.error('Grid Service -> downloadClientSideData', error);
    return Promise.reject(error);
  }
};

export const generateReport = async (
  fileName: CreditTransferOrder['orderId'],
  uniqueId: CreditTransferOrder['uniqueId'],
  sortBy: string,
  reportType: CreditTransferReportType,
  reportSubType: CreditTransferReportType,
  data: JsonObject
): Promise<void> => {
  const params: Params = {
    reportType,
  };
  if (sortBy) params.sortBy = sortBy;
  if (reportSubType) params.reportSubType = reportSubType;

  const url = `${endpoints.EFTApiUrl}/${endpoints.orders}/${uniqueId}/${endpoints.generateReport}`;

  try {
    const response: AxiosResponse = await axiosInstance.put(url, data, {
      params,
      responseType: 'arraybuffer',
    });
    return saveFile(response.data, fileName, FILE_TYPE.PDF);
  } catch (error) {
    Log.error('Grid Service -> generateReport', error);
    return Promise.reject(error);
  }
};
